import React, { useMemo, useState } from 'react';
import { ChevronDown, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Bemanning } from '@/services/api/types';
import { cn } from '@/lib/utils';

interface CollapsibleStaffingTableProps {
  bemanning: Bemanning[];
  className?: string;
}

interface GroupedStaffing {
  categoryName: string;
  talents: {
    talentName: string;
    people: {
      firstName: string;
      lastName: string;
      status: string;
    }[];
  }[];
}

/**
 * Hjelpe-funksjon for å hente topp-kategori fra kategori-sti
 * F.eks. "Kreativ/Innhold → Lyd" -> "Kreativ/Innhold"
 */
const getTopCategory = (kategoriSti: string): string => {
  if (!kategoriSti) return 'Ukategorisert';
  const parts = kategoriSti.split('→').map(p => p.trim());
  return parts[0] || 'Ukategorisert';
};

/**
 * Hent status badge variant basert på status
 */
const getStatusBadge = (status: string) => {
  switch (status) {
    case 'bekreftet':
      return (
        <Badge className="bg-status-confirmed text-white shrink-0 text-xs">
          Bekreftet
        </Badge>
      );
    case 'avvist':
      return (
        <Badge className="bg-status-declined text-white shrink-0 text-xs">
          Avvist
        </Badge>
      );
    case 'planlagt':
    default:
      return (
        <Badge className="bg-status-waiting text-white shrink-0 text-xs">
          Avventer
        </Badge>
      );
  }
};

export const CollapsibleStaffingTable: React.FC<CollapsibleStaffingTableProps> = ({
  bemanning,
  className,
}) => {
  // Gruppér bemanning etter topp-kategori
  const groupedData = useMemo<GroupedStaffing[]>(() => {
    if (!bemanning || bemanning.length === 0) return [];

    // Gruppér etter topp-kategori
    const categoryMap = new Map<string, Map<string, Bemanning[]>>();

    bemanning.forEach((b) => {
      const topCategory = getTopCategory(b.talent_kategori);
      const talentName = b.talent_navn;

      if (!categoryMap.has(topCategory)) {
        categoryMap.set(topCategory, new Map());
      }

      const talentMap = categoryMap.get(topCategory)!;
      if (!talentMap.has(talentName)) {
        talentMap.set(talentName, []);
      }

      talentMap.get(talentName)!.push(b);
    });

    // Konverter til array og sorter
    const result: GroupedStaffing[] = [];

    categoryMap.forEach((talentMap, categoryName) => {
      const talents: GroupedStaffing['talents'] = [];

      // Sorter talenter alfabetisk
      const sortedTalents = Array.from(talentMap.keys()).sort((a, b) =>
        a.localeCompare(b, 'no')
      );

      sortedTalents.forEach((talentName) => {
        const people = talentMap.get(talentName)!.map((b) => ({
          firstName: b.first_name,
          lastName: b.last_name,
          status: b.status,
        }));

        // Sorter personer alfabetisk etter etternavn, deretter fornavn
        people.sort((a, b) => {
          const lastNameCompare = a.lastName.localeCompare(b.lastName, 'no');
          if (lastNameCompare !== 0) return lastNameCompare;
          return a.firstName.localeCompare(b.firstName, 'no');
        });

        talents.push({
          talentName,
          people,
        });
      });

      result.push({
        categoryName,
        talents,
      });
    });

    // Sorter kategorier alfabetisk
    result.sort((a, b) => a.categoryName.localeCompare(b.categoryName, 'no'));

    return result;
  }, [bemanning]);

  // State for å holde styr på åpne/lukkede kategorier
  const [openCategories, setOpenCategories] = useState<Set<string>>(() => {
    // Default: Alle ekspandert
    return new Set(groupedData.map((g) => g.categoryName));
  });

  const toggleCategory = (categoryName: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  if (!bemanning || bemanning.length === 0) {
    return (
      <Card className={cn('p-6', className)}>
        <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground py-8">
          <Users className="h-12 w-12 opacity-50" />
          <p className="text-sm">Ingen bemanning registrert</p>
        </div>
      </Card>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {groupedData.map((category) => {
        const isOpen = openCategories.has(category.categoryName);
        const totalPeople = category.talents.reduce(
          (sum, t) => sum + t.people.length,
          0
        );

        return (
          <Card
            key={category.categoryName}
            className="overflow-hidden shadow-sm hover:shadow-md transition-shadow"
          >
            <Collapsible open={isOpen} onOpenChange={() => toggleCategory(category.categoryName)}>
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground transition-transform shrink-0',
                        isOpen ? 'rotate-0' : '-rotate-90'
                      )}
                    />
                    <h3 className="text-sm sm:text-base font-semibold text-foreground truncate">
                      {category.categoryName}
                    </h3>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-primary/10 text-primary border-primary/20 shrink-0 text-xs ml-2"
                  >
                    {totalPeople} {totalPeople === 1 ? 'person' : 'personer'}
                  </Badge>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="border-t">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs sm:text-sm">Talent</TableHead>
                        <TableHead className="text-xs sm:text-sm">Person</TableHead>
                        <TableHead className="text-right text-xs sm:text-sm">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {category.talents.map((talent) =>
                        talent.people.map((person, personIdx) => (
                          <TableRow
                            key={`${talent.talentName}-${person.firstName}-${person.lastName}-${personIdx}`}
                            className="hover:bg-muted/30"
                          >
                            <TableCell className="font-medium text-xs sm:text-sm">
                              <span className="truncate block">
                                {talent.talentName}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs sm:text-sm">
                              <span className="truncate block">
                                {person.firstName} {person.lastName}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {getStatusBadge(person.status)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
};

export default CollapsibleStaffingTable;

